// Optional Google sign-in: who is signed in, for the organiser pages.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { SessionUser } from '@shared/types';
import { api, ApiError, onAccountChanged, onUnauthorized } from './api';
import { useAppConfig, useConfigLoaded } from './config';

export type AuthStatus = 'disabled' | 'loading' | 'signedOut' | 'signedIn';

export interface AuthState {
  /**
   * 'disabled' when this server has no Google sign-in: every account UI stays hidden. 'loading'
   * until the server's config and (with sign-in) the session are known.
   */
  status: AuthStatus;
  user: SessionUser | null;
  /** Re-reads the session from the server. */
  refresh: () => Promise<void>;
  /** Ends this browser's session. Never touches the quizzes saved in this browser. */
  signOut: () => Promise<void>;
  /**
   * Counts changes of the signed-in account once it is known (sign in, sign out, another account).
   * The first answer from the server is not a change.
   */
  sessionChanges: number;
  /** Call when a sign-in opens in a new tab: coming back to this tab then re-reads the session at once. */
  expectSignIn: () => void;
}

interface AuthContextValue {
  state: AuthState;
  /** Asks for the session (it is fetched lazily, once a page needs it). */
  want: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Coming back to the tab re-reads the session at most this often (it may have changed in another tab). */
const FOCUS_REFRESH_MS = 30_000;

const noop = async () => undefined;
const DISABLED: AuthState = {
  status: 'disabled',
  user: null,
  refresh: noop,
  signOut: noop,
  sessionChanges: 0,
  expectSignIn: () => undefined,
};

/**
 * Goes inside ConfigProvider. `/api/auth/session` is fetched only once a component calls
 * `useAuth()` and the server has sign-in, so the player and projector pages never ask.
 * `initial` (tests) starts with a known session and fetches nothing.
 */
export function AuthProvider({ children, initial }: { children: ReactNode; initial?: SessionUser | null }) {
  const { googleSignIn } = useAppConfig();
  const configLoaded = useConfigLoaded();
  const [session, setSession] = useState<{ loaded: boolean; user: SessionUser | null }>(() =>
    initial === undefined ? { loaded: false, user: null } : { loaded: true, user: initial },
  );
  const [wanted, setWanted] = useState(false);
  const [sessionChanges, setSessionChanges] = useState(0);
  /** The settled account id (null = signed out); undefined until the server first answered. */
  const settledId = useRef<string | null | undefined>(initial === undefined ? undefined : (initial?.id ?? null));
  /** Bumped by every fetch and on cleanup, so an older answer never overwrites a newer one. */
  const generation = useRef(0);
  const lastFetchAt = useRef(0);

  const apply = useCallback((user: SessionUser | null) => {
    const id = user?.id ?? null;
    if (settledId.current !== undefined && settledId.current !== id) setSessionChanges((n) => n + 1);
    settledId.current = id;
    setSession({ loaded: true, user });
  }, []);

  const refresh = useCallback(async () => {
    const gen = ++generation.current;
    lastFetchAt.current = Date.now();
    try {
      const user = await api.session();
      if (gen === generation.current) apply(user);
    } catch {
      // Network trouble says nothing about the session: keep what we know (signed out if nothing yet).
      if (gen === generation.current && settledId.current === undefined) apply(null);
    }
  }, [apply]);

  const signOut = useCallback(async () => {
    try {
      await api.signOut();
    } catch (err) {
      // Already signed out (or sign-in is off): the result is the same.
      if (!(err instanceof ApiError && (err.status === 401 || err.status === 404))) throw err;
    }
    generation.current++;
    apply(null);
  }, [apply]);

  const expectSignIn = useCallback(() => {
    lastFetchAt.current = 0;
  }, []);

  const active = googleSignIn && wanted;

  // First load (lazy). The generation bump on cleanup is the cancel flag (StrictMode runs this twice).
  useEffect(() => {
    if (!active || session.loaded) return;
    void refresh();
    return () => {
      generation.current++;
    };
  }, [active, session.loaded, refresh]);

  // Any 401 means the session ended somewhere, and a 409 from an account route that another account
  // is signed in now (another tab): find out who we are now.
  useEffect(() => {
    if (!active) return;
    const unsubscribe = [onUnauthorized(() => void refresh()), onAccountChanged(() => void refresh())];
    return () => unsubscribe.forEach((off) => off());
  }, [active, refresh]);

  // Coming back to the tab re-reads the session: another tab may have signed in, out, or in to
  // another account. Account writes also name the account the page shows (api.ts), so a tab that
  // has not caught up yet can never act on another account.
  useEffect(() => {
    if (!active || !session.loaded) return;
    const onFocus = () => {
      if (Date.now() - lastFetchAt.current >= FOCUS_REFRESH_MS) void refresh();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [active, session.loaded, refresh]);

  const want = useCallback(() => setWanted(true), []);
  const value = useMemo<AuthContextValue>(() => {
    const status: AuthStatus = !configLoaded
      ? 'loading'
      : !googleSignIn
        ? 'disabled'
        : !session.loaded
          ? 'loading'
          : session.user
            ? 'signedIn'
            : 'signedOut';
    return {
      want,
      state: {
        status,
        user: status === 'signedIn' ? session.user : null,
        refresh,
        signOut,
        sessionChanges,
        expectSignIn,
      },
    };
  }, [configLoaded, googleSignIn, session, sessionChanges, want, refresh, signOut, expectSignIn]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** The signed-in account. Without an AuthProvider (or without sign-in on the server) it is 'disabled'. */
export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  const want = ctx?.want;
  useEffect(() => {
    want?.();
  }, [want]);
  return ctx?.state ?? DISABLED;
}

/** Runs `onChange` when the signed-in account changes after the page loaded (not on the first answer). */
export function useSessionChange(onChange: (user: SessionUser | null) => void): void {
  const { sessionChanges, user } = useAuth();
  const seen = useRef(sessionChanges);
  const callback = useRef(onChange);
  useLayoutEffect(() => {
    callback.current = onChange;
  });
  useEffect(() => {
    if (sessionChanges === seen.current) return;
    seen.current = sessionChanges;
    callback.current(user);
  }, [sessionChanges, user]);
}
